import { useState } from "react";
import { Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useApiData from "@/hooks/useApiData";
import FilterBar from "@/components/FilterBar";
import ListState from "@/components/ListState";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "warmup", label: "Warm-Up" },
  { id: "stretching", label: "Stretching" },
  { id: "footwork", label: "Footwork" },
  { id: "strength", label: "Strength" },
  { id: "recovery", label: "Recovery" },
  { id: "technique", label: "Technique" },
];

const ResourceItem = ({ resource, expanded, onToggle }) => (
  <div className="border-b border-[#1D1F21]" data-testid={`resource-${resource.id}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between py-5 text-left">
      <div>
        <h3 className="font-display text-base font-bold text-[#F2EDE4]">{resource.title}</h3>
        <div className="flex items-center gap-3 mt-1">
          <span className="font-mono text-[10px] text-[#0D6B56] capitalize tracking-wider">{resource.category}</span>
          <span className="text-xs text-[#F2EDE4]/20 flex items-center gap-1"><Clock className="w-3 h-3" /> {resource.duration}</span>
        </div>
      </div>
      {expanded ? <ChevronUp className="w-4 h-4 text-[#F2EDE4]/20 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#F2EDE4]/20 flex-shrink-0" />}
    </button>
    {expanded && (
      <div className="pb-6">
        <p className="text-sm text-[#F2EDE4]/30 mb-4">{resource.description}</p>
        <ol className="space-y-2">
          {resource.instructions?.map((step, i) => (
            <li key={step} className="text-sm text-[#F2EDE4]/40 flex gap-3">
              <span className="font-mono text-[10px] text-[#F2EDE4]/20 w-5 flex-shrink-0 mt-0.5">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    )}
  </div>
);

const Training = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedItem, setExpandedItem] = useState(null);
  const { data: resources, loading } = useApiData(api.getTrainingResources, activeCategory === "all" ? null : activeCategory);

  return (
    <div className="pt-16" data-testid="training-page" style={{ background: "var(--lrg-bg)" }}>
      <section className="py-16" style={{ background: "var(--lrg-surface)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Movement Library</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F2EDE4] mb-3">Training Resources</h1>
          <p className="text-[#F2EDE4]/40 max-w-md">Drills, routines, and conditioning for the squash athlete.</p>
        </div>
      </section>

      <FilterBar options={CATEGORIES} active={activeCategory} onChange={setActiveCategory} />

      <section className="section" data-testid="training-resources">
        <div className="container-custom">
          <ListState loading={loading} isEmpty={resources.length === 0} emptyText="No resources found.">
            <div className="max-w-3xl mx-auto space-y-0">
              {resources.map((resource) => (
                <ResourceItem
                  key={resource.id}
                  resource={resource}
                  expanded={expandedItem === resource.id}
                  onToggle={() => setExpandedItem(expandedItem === resource.id ? null : resource.id)}
                />
              ))}
            </div>
          </ListState>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--lrg-surface)" }} data-testid="training-rates">
        <div className="container-custom max-w-2xl text-center">
          <div className="overline mb-3">Private Sessions</div>
          <h2 className="font-display text-2xl font-bold text-[#F2EDE4] mb-2">Individual Coaching</h2>
          <p className="text-sm text-[#F2EDE4]/30 mb-6">By appointment. Semi-privates also available. Contact our manager for availability and rates.</p>
          <Link to="/contact" className="btn-primary" data-testid="training-contact-cta">Contact Our Manager</Link>
        </div>
      </section>
    </div>
  );
};

export default Training;
